import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { OrderRestControllerService, RestaurantBranchEntity, RestaurantBranchRestControllerService, SmartDineResponseOrderSummaryModel, SmartDineResponseRestaurantBranchEntity } from 'src/app/swagger';
import { LoaderService } from '../../services/loader/loader.service';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'order-display-screen',
  templateUrl: './order-display-screen.component.html',
  styleUrls: ['./order-display-screen.component.scss'],
  standalone: false,
})
export class OrderDisplayScreenComponent implements OnInit {
  public restaurantId: string | null = null;
  public branchId: string | null = null;
  public branches: RestaurantBranchEntity[] = [];
  public statusList: string[] = ['ACCEPTED', 'PREPARING', 'PREPARED', 'DELIVERED', 'PICKED_UP', 'SUBMITTED', 'CANCELLED', 'NOT_ACCEPTED'];
  public orders: SmallOrder[] = [];
  public fromDate!: Date;
  public toDate!: Date;

  constructor(
    private orderRestControllerService: OrderRestControllerService,
    private router: Router,
    private loaderService: LoaderService,
    private activatedRoute: ActivatedRoute,
    private restaurantService: RestaurantBranchRestControllerService
  ) {
    this.activatedRoute.queryParams.subscribe(params => {
      this.restaurantId = params['restaurantId'] || null;
    });
  }

  public async ngOnInit(): Promise<void> {
    await this.loadBranches();
  }
  
  public onBackClick(): void {
    const restaurantId = this.restaurantId;
    this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
      this.router.navigate(['/resto-admin/home'], {
        queryParams: { restaurantId },
      });
    });
  }

  public onBranchChange(event: any): void {
    const selectedBranchId = event.detail?.value;
    this.branchId = selectedBranchId;

    this.toDate = new Date();
    this.fromDate = new Date(this.toDate);
    this.fromDate.setDate(this.toDate.getDate() - 1);

    this.getLiveOrders();
  }

  public statusClass(status: string | undefined): string {
    if (!status) return 'status-active';
  
    const normalized = status.toLowerCase();
  
    if (normalized === 'delivered' || normalized === 'picked_up' || normalized === 'completed') {
      return 'status-delivered';
    } else if (normalized === 'cancelled' || normalized === 'declined' || normalized === 'not_accepted') {
      return 'status-cancelled';
    } else {
      return 'status-active';
    }
  }
  
  public statusProgress(status: string): number {
    switch (status?.toLowerCase()) {
      case 'submitted': return 20;
      case 'accepted': return 40;
      case 'preparing': return 60;
      case 'prepared': return 80;
      case 'delivered':
      case 'completed':
      case 'picked_up':
        return 100;
      case 'cancelled':
      case 'not_accepted':
      case 'declined':
        return 100;
      default: return 10;
    }
  }
  
  public progressColorClass(status: string): string {
    if (!status) return 'progress-blue';
    const s = status.toLowerCase();
    if (s === 'delivered' || s === 'completed' || s === 'picked_up') return 'progress-green';
    if (s === 'cancelled' || s === 'not_accepted') return 'progress-red';
    return 'progress-blue';
  }
  
  public sortedOrders() {
    return [...(this.orders || [])].sort((a, b) =>
      new Date(b?.createdAt || '').getTime() - new Date(a?.createdAt || '').getTime()
    );
  }

  public formatCurrency(amount: number | undefined): string {
    return (amount ?? 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' });
  }

  public timeSince(dateStr: string | undefined): string {
    if (!dateStr) return '—';
    const created = new Date(dateStr).getTime();
    const now = Date.now();
    const diffSec = Math.floor((now - created) / 1000);
    const diffMin = Math.floor(diffSec / 60);
    const diffHr = Math.floor(diffMin / 60);
    const diffDay = Math.floor(diffHr / 24);
    const diffMonth = Math.floor(diffDay / 30);

    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin} min ago`;
    if (diffHr < 24) return `${diffHr} hr${diffHr > 1 ? 's' : ''} ago`;
    if (diffDay < 30) return `${diffDay} day${diffDay > 1 ? 's' : ''} ago`;
    return `${diffMonth} month${diffMonth > 1 ? 's' : ''} ago`;
  }

  public maskedCustomer(name: string | undefined): string {
    if (!name) return '—';
    return name.trim().split(' ')[0];
  }

  private async getLiveOrders(): Promise<void> {
    this.orders = [];

    if (!this.isValidOrderParams()) return;

    this.loaderService.showLoader('Loading Live Orders...');
    const response: SmartDineResponseOrderSummaryModel = await lastValueFrom(
      this.orderRestControllerService.getOrders1(this.restaurantId, this.branchId, this.statusList, this.fromDate, this.toDate, null, 1, 10, ['DESC:createdAt'])
    );

    this.orders = (response?.items || []).map(o => this.mapToSmallOrder(o));
    this.loaderService.hideLoader();
  }

  private isValidOrderParams(): boolean {
    const isValidDate = (date: any): boolean =>
      date instanceof Date && !isNaN(date.getTime());

    if (!this.restaurantId || !this.branchId) {
      return false;
    }

    if (!isValidDate(this.fromDate) || !isValidDate(this.toDate)) {
      return false;
    }

    if (!Array.isArray(this.statusList) || this.statusList.length === 0) {
      return false;
    }

    return true;
  }

  private async loadBranches(): Promise<void> {
    this.branches = [];

    if (!this.restaurantId) return;

    this.loaderService.showLoader('Loading Branches...');
    const restaurantDetails: SmartDineResponseRestaurantBranchEntity = await lastValueFrom(this.restaurantService.getRestaurantBranches1(this.restaurantId));
    this.branches = restaurantDetails?.items || [];
    this.loaderService.hideLoader();
  }

  private mapToSmallOrder(order: any): SmallOrder {
    if (!order) return {} as SmallOrder;
  
    return {
      orderId: order?.orderId,
      orderNumber: order?.orderNumber,
      orderType: order?.orderType,
      restaurantModel: {name: order?.restaurantModel?.name || '—'},
      restaurantBranchModel: {name: order?.restaurantBranchModel?.name || '—'},
      table: order?.restaurantBranchTableModel?.tableCode || null,
      orderStatus: order?.orderStatus,
      orderPaymentStatus: order?.orderPaymentStatus,
      createdAt: order?.createdAt,
      orderPriceModel: {totalPriceWithDiscountAndTax: order?.orderPriceModel?.totalPriceWithDiscountAndTax || 0},
      orderItemModels: order?.orderItemModels || {},
      customerName: order?.createdBy?.firstName && order?.createdBy?.lastName ? `${order.createdBy.firstName} ${order.createdBy.lastName}` : order?.createdBy?.firstName || order?.customerName || '—',
    };
  }

}

export type SmallOrder = {
  orderId: string;
  orderNumber: number;
  orderType?: string;
  restaurantModel?: { name?: string };
  restaurantBranchModel?: { name?: string };
  table?: string | null;
  orderStatus: string;
  orderPaymentStatus?: string;
  createdAt?: string; // ISO date
  orderPriceModel?: { totalPriceWithDiscountAndTax?: number };
  orderItemModels?: Record<string, any>;
  customerName?: string; // will NOT be shown in full
};
